import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import GlobalConfig from "../context/GlobalConfig";
// import { ArrowPathIcon } from '@heroicons/react/24/solid';
import Footer from "../components/Footer";
import Head from "./Head";
import { languageData } from "../constants/languageData";
import CameraAccessDenied from "./CameraAccessDenied";
import JourneyConfig from "../context/JourneyConfig";
import { useParams, useSearchParams } from "react-router-dom";
import GoogleIcon from "../components/GoogleIcon";
import { trackAction } from "../utils/hyperdx";
import { captureLocationApi } from "../utils/captureLocationApi";

function CameraAccess({ stepperFunc, isMobile ,steps, navigation, currentDocumentStep }) {
  const { config, routesAndNavigation } = useContext(GlobalConfig);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const { journey, setJourney, getJourney } = useContext(JourneyConfig);
  const { journeyId } = useParams();
  const language = sessionStorage.getItem("language") ?? "en";
  const deviceContext = JSON.parse(sessionStorage.getItem("deviceContext"));
  const { journeyId: journeyIdFromParams } = useParams();
  const jId = sessionStorage.getItem("jId") ?? journeyIdFromParams;
  const queryParams = useSearchParams()[0];
  const source = queryParams.get("source");
  const [userLocation, setUserLocation] = useState(null);
  
  const browserKeyMap = {
    "Android WebView": "androidWebViewPermissionDenied",
    "Android Browser": "androidBrowserPermissionDenied",
    "iOS WebView": "iosWebViewPermissionDenied",
    "iOS Browser": "iosBrowserPermissionDenied",
    "Desktop Browser": "desktopBrowserPermissionDenied",
    Unknown: "permissionDenied",
  };
  const browserTypeKey = browserKeyMap[deviceContext] || "permissionDenied";

  // Request camera and location access

  const requestCameraAndLocationPermission = useCallback(async () => {
    try {
      let permissionGranted = false;
      
      if (navigator.permissions?.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          permissionGranted = permissionStatus.state === 'granted';
          console.log(`[CameraAccess] Permissions API state: ${permissionStatus?.state}`);
          permissionGranted = permissionStatus.state === 'granted' || sessionStorage.getItem('cameraAccessAllowed') === 'true';
          if(permissionGranted) {
            console.log('[CameraAccess] Permissions Already granted . No need to request for permission again.');
          }
        } catch (e) {
          permissionGranted = sessionStorage.getItem('cameraAccessAllowed') === 'true';
          console.log('[CameraAccess] Permissions API not fully supported, proceeding...');
        }
      } else {
        console.log('[CameraAccess] Permissions API not available, falling back to sessionStorage');
        permissionGranted = sessionStorage.getItem('cameraAccessAllowed') === 'true';
      }

      // 2. Request camera only if needed
      if (!permissionGranted) {
        console.log('[CameraAccess] Permissions have not granted. Requesting for camera permission.');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: isMobile ? { min: 720, ideal: 1920 } : { min: 720, ideal: 1280 },
            height: isMobile ? { min: 720, ideal: 1080 } : { min: 720, ideal: 720 },
            facingMode: isMobile ? { ideal: "environment" } : { ideal: "user" },
          },
        });
        // Immediately release camera
        stream.getTracks().forEach(track => {
          console.log("[CameraAccess] Stopping track:", track?.kind, track?.id); 
          track.stop();
          console.log("[CameraAccess] Track stopped:", track?.readyState);
        });
        
        sessionStorage.setItem('cameraAccessAllowed', 'true');
        trackAction("camera:permission", { granted: true });
      }

      // Capture location in the background (fire-and-forget) so it never blocks
      // or hangs the camera step.
      getUserLocation();
      if (source === "Digilocker") {
        if (jId) {
          getJourney(jId);
        }
        stepperFunc(11);
      } else {
        stepperFunc(steps[0]);
      }
    } catch (error) {
      const errorDetails = {
        name: error.name,
        message: error.message,
        code: error.code,
        constraint: error.constraint,
        permissionDenied: error.name === 'NotAllowedError',
        deviceNotFound: error.name === 'NotFoundError',
        stack: error.stack
      };
      console.error("Camera access failed:", JSON.stringify(errorDetails, null, 2));
      trackAction("camera:permission", {
        granted: false,
        reason: error?.name,
        permissionDenied: errorDetails.permissionDenied,
        deviceNotFound: errorDetails.deviceNotFound,
      });
      getUserLocation();
      setPermissionDenied(true);
    }
  },[]);

  const getUserLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!config?.userSettings?.allowLocation) {
        console.log("Location access is disabled in user settings.");
        resolve();
        return;
      }
      if (
        journey?.capturedData?.location?.latitude != null &&
        journey?.capturedData?.location?.longitude != null
      ) {
        console.log("Location already captured, skipping geolocation request.");
        resolve();
        return;
      }
      if (!navigator.geolocation) {
        console.error("Geolocation is not supported by this browser.");
        resolve();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          trackAction("location:permission", { granted: true });
          setUserLocation({ latitude, longitude });
          setJourney((prev) => ({
            ...prev,
            capturedData: {
              ...(prev?.capturedData || {}),
              location: { latitude, longitude },
            },
          }));
          // Fire-and-forget persistence — dedicated endpoint, no journey
          // state-machine side effects, never blocks/interferes with the journey.
          captureLocationApi({ journeyId: jId, location: { latitude, longitude } });
          resolve();
        },
        (error) => {
          console.error("Error fetching location:", error.message);
          const permissionDenied = error?.code === 1; // PERMISSION_DENIED
          trackAction("location:permission", {
            granted: false,
            reason: error?.message,
            permissionDenied: error?.code === 1, // PERMISSION_DENIED
          })
          // Fire-and-forget persistence of the failure so the journey records why
          // location is absent. We send the raw geolocation error.code (1/2/3) and the
          // backend maps it to the appropriate status + reason.
          captureLocationApi({
            journeyId: jId,
            location: { code: error?.code },
          });
          resolve();
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }, [config?.userSettings?.allowLocation, journey, setJourney, jId]);

  useEffect(() => {
    requestCameraAndLocationPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkClick = () => {
    const userAgent = navigator.userAgent;
    let supportUrl;
  
    if (userAgent.includes("Chrome") && !userAgent.includes("Edg/")) {
      // This ensures it's Chrome, not Edge
      supportUrl = "https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop&oco=0";
    } else if (userAgent.includes("Edg/")) {
      // Edge specifically
      supportUrl = "https://help.doxy.me/en/articles/3867526-camera-and-microphone-permission-edge";
    } else if (userAgent.includes("Firefox")) {
      supportUrl = "https://help.doxy.me/en/articles/2427185-camera-and-microphone-permission-firefox";
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      supportUrl = "https://help.doxy.me/en/articles/836274-camera-and-microphone-permission-safari";
    } else {
      // Default Chrome link
      supportUrl = "https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop&oco=0";
    }
  
    window.open(supportUrl, "_blank");
  };

  useEffect(() => {
    if (journeyId && !journey) {
      getJourney(journeyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, journey, getJourney]);

  if (permissionDenied) {
    return <CameraAccessDenied {...{ stepperFunc, isMobile, steps , deviceContext}} />;
  }

  return (
    <>
      <Head
        showBackIcon
        handleBack={() => stepperFunc(steps[2])}
        showMobileText
        showMobileFlow
        style={{ zIndex: "100", position: "relative", top: 0 }}
        stepperFunc={stepperFunc}
        showLanguages={false}
      />
      <div className="parent-container" style={{ paddingTop: 0 }}>
        <div className="main">
          <div className="left-section">
            <GoogleIcon iconName="videocam_off" size={48} style={{ color: "#656565" }}/>
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              gap:"8px",
              justifyContent: "space-between",
              fontFamily: "Inter, sans-serif",

            }}
          >
            <div className="flex flex-col w-full">
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {
                  languageData[language]?.[browserTypeKey]
                    ?.title
                }
              </div>
              <div className="md:mt-3 mt-6 mb-4 text-[12px] md:text-[14px] text-[#656565] text-center md:text-left  font-inter text-sm font-semibold leading-normal">
                {
                  languageData[language]?.[browserTypeKey]
                    ?.subtitle
                }
              </div>
              <button
                onClick={handleLinkClick}
                style={{
                  color: "#1658aa", // Dynamic text color
                  textDecoration: "none", // Remove underline
                  background: "none", // Remove button background
                  border: "none", // Remove border
                  padding: "0", // Remove padding to mimic a link
                  cursor: "pointer", // Pointer cursor to indicate clickability
                  textAlign: "inherit", // Inherit text alignment
                  display: "inline-flex", // Use inline-flex to make them inline and allow flex properties
                  alignItems: "center", // Vertically align text and icon in the center
                }}
                className="font-inter text-sm font-semibold leading-normal justify-center md:justify-start"
              >
                <>
                  {languageData[language]?.[browserTypeKey]?.readMore}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="#1658aa"
                    className="ml-2"
                  >
                    <g clipPath="url(#clip0_5376_103561)">
                      <path
                        d="M10.5 11.0833H3.5C3.17917 11.0833 2.91667 10.8208 2.91667 10.5V3.5C2.91667 3.17917 3.17917 2.91667 3.5 2.91667H6.41667C6.7375 2.91667 7 2.65417 7 2.33333C7 2.0125 6.7375 1.75 6.41667 1.75H2.91667C2.26917 1.75 1.75 2.275 1.75 2.91667V11.0833C1.75 11.725 2.275 12.25 2.91667 12.25H11.0833C11.725 12.25 12.25 11.725 12.25 11.0833V7.58333C12.25 7.2625 11.9875 7 11.6667 7C11.3458 7 11.0833 7.2625 11.0833 7.58333V10.5C11.0833 10.8208 10.8208 11.0833 10.5 11.0833ZM8.16667 2.33333C8.16667 2.65417 8.42917 2.91667 8.75 2.91667H10.2608L4.935 8.2425C4.7075 8.47 4.7075 8.8375 4.935 9.065C5.1625 9.2925 5.53 9.2925 5.7575 9.065L11.0833 3.73917V5.25C11.0833 5.57083 11.3458 5.83333 11.6667 5.83333C11.9875 5.83333 12.25 5.57083 12.25 5.25V2.33333C12.25 2.0125 11.9875 1.75 11.6667 1.75H8.75C8.42917 1.75 8.16667 2.0125 8.16667 2.33333Z"
                        fill="#1658aa"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5376_103561">
                        <rect width="14" height="14" fill="#1658aa" />
                      </clipPath>
                    </defs>
                  </svg>
                </>
              </button>

            </div>
            {/* <div className="flex flex-col justify-center items-center w-full">
              <button
                className="flex flex-row items-center justify-center gap-4 w-full text-white py-4 px-4 rounded text-white"
                style={{ backgroundColor: config?.branding?.accentColor }}
                onClick={() => {
                  const enableMobileFlowKey = Object.keys(routesAndNavigation?.routes).find(
                    (key) => routesAndNavigation?.routes[key].includes("enable-mobile-flow")
                  );
                  if (enableMobileFlowKey) {
                    stepperFunc(enableMobileFlowKey);
                  }
                }}
              >
                {
                  languageData[language]?.permissionDenied
                    ?.buttonText
                }
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="add_to_home_screen" clip-path="url(#clip0_2274_22553)">
                    <path
                      id="Vector"
                      d="M12 0.673659L5.33333 0.666992C4.6 0.666992 4 1.26699 4 2.00033V4.00033H5.33333V3.33366H12V12.667H5.33333V12.0003H4V14.0003C4 14.7337 4.6 15.3337 5.33333 15.3337H12C12.7333 15.3337 13.3333 14.7337 13.3333 14.0003V2.00033C13.3333 1.26699 12.7333 0.673659 12 0.673659ZM6.66667 10.0003H8V5.33366H3.33333V6.66699H5.72667L2 10.3937L2.94 11.3337L6.66667 7.60699V10.0003Z"
                      fill={"white"}
                      style={{ fill: "white" }}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2274_22553">
                      <rect width="16" height="16" fill="#1658aa" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
              <Footer />
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
}

export default CameraAccess;
